import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import Toast, { useToast } from '../components/Toast';

// ============================================================================
// RefundRequestPage — customer self-service refund request
// Route: /r/:sessionId/refund   (suggested; wire up in App.js)
// ============================================================================

const formatWon = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const REASONS = [
  { key: 'wrong_order', label: '잘못 주문했어요' },
  { key: 'quality', label: '품질에 문제가 있어요' },
  { key: 'late', label: '제공이 너무 늦어요' },
  { key: 'other', label: '기타' },
];

const STATUS_LABELS = {
  pending: { label: '검토 대기', color: 'var(--warning, #f59e0b)' },
  approved: { label: '승인됨', color: 'var(--primary)' },
  processing: { label: '처리 중', color: 'var(--primary)' },
  completed: { label: '환불 완료', color: 'var(--success, #10b981)' },
  rejected: { label: '거절됨', color: 'var(--danger, #ef4444)' },
  failed: { label: '실패', color: 'var(--danger, #ef4444)' },
};

export default function RefundRequestPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [myRefunds, setMyRefunds] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [reason, setReason] = useState('wrong_order');
  const [detail, setDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [sumRes, payRes, refRes] = await Promise.all([
        api.get(`/sessions/${sessionId}/summary`).catch(() => null),
        api.get(`/sessions/${sessionId}/payment-status`).catch(() => null),
        api.get(`/sessions/${sessionId}/refunds`).catch(() => null),
      ]);
      const sumData = sumRes?.data || sumRes;
      const payData = payRes?.data || payRes;
      const refData = refRes?.data || refRes;
      setSummary(sumData || null);
      setPayments((payData?.payments || []).filter((p) => p.status === 'completed'));
      setMyRefunds(refData?.refunds || []);
    } catch {
      showToast('정보를 불러오지 못했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [sessionId, showToast]);

  useEffect(() => { load(); }, [load]);

  const openModal = (payment, orderId) => {
    setSelectedPayment(payment);
    setSelectedOrderId(orderId || null);
    setReason('wrong_order');
    setDetail('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPayment(null);
    setSelectedOrderId(null);
  };

  const submit = async () => {
    if (!selectedPayment) return;
    setIsSubmitting(true);
    try {
      await api.post('/refunds/request', {
        payment_id: selectedPayment.id,
        order_id: selectedOrderId || undefined,
        session_id: sessionId,
        reason,
        reason_detail: detail.trim() || undefined,
      });
      showToast('환불 요청이 접수되었습니다', 'success');
      closeModal();
      load();
    } catch (err) {
      showToast(err.message || '환불 요청에 실패했습니다', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Derive refund status per payment
  const refundByPayment = {};
  myRefunds.forEach((r) => {
    if (!refundByPayment[r.payment_id] || r.created_at > refundByPayment[r.payment_id].created_at) {
      refundByPayment[r.payment_id] = r;
    }
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #f5f5f5)', paddingBottom: 80 }}>
      <Toast toast={toast} />

      {/* Header */}
      <div
        style={{
          background: 'white',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid var(--border, #eee)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--text, #111)' }}
          aria-label="뒤로"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>환불 요청</h1>
      </div>

      <div style={{ padding: 16 }}>
        {payments.length === 0 ? (
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--text-tertiary, #999)',
            }}
          >
            환불 가능한 결제 내역이 없습니다.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {payments.map((p) => {
              const existingRefund = refundByPayment[p.id];
              return (
                <div
                  key={p.id}
                  style={{
                    background: 'white',
                    borderRadius: 16,
                    padding: 16,
                    border: '1px solid var(--border, #eee)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-tertiary, #888)' }}>
                      {formatDateTime(p.created_at)}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text, #111)' }}>
                      {formatWon(p.amount)}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary, #888)', marginBottom: 12 }}>
                    결제수단: {p.method || '카드'} · ID: {p.id.slice(0, 8)}
                  </div>
                  {existingRefund ? (
                    <div
                      style={{
                        padding: '12px 14px',
                        background: 'var(--bg, #f5f5f5)',
                        borderRadius: 10,
                        fontSize: 13,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>환불 상태</span>
                        <strong style={{ color: (STATUS_LABELS[existingRefund.status] || {}).color }}>
                          {(STATUS_LABELS[existingRefund.status] || {}).label || existingRefund.status}
                        </strong>
                      </div>
                      {existingRefund.failure_reason && (
                        <div style={{ marginTop: 6, color: 'var(--danger, #ef4444)', fontSize: 12 }}>
                          사유: {existingRefund.failure_reason}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => openModal(p, null)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      환불 요청
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedPayment && (
        <>
          <div className="overlay" onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100 }} />
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'white',
              borderRadius: '20px 20px 0 0',
              padding: '20px 20px 32px',
              zIndex: 101,
              maxHeight: '85vh',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                background: 'var(--border, #e5e5e5)',
                borderRadius: 2,
                margin: '0 auto 16px',
              }}
            />
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>환불 요청</h2>
            <p style={{ fontSize: 13, color: 'var(--text-tertiary, #888)', marginBottom: 16 }}>
              {formatWon(selectedPayment.amount)} 환불을 요청합니다
            </p>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>환불 사유</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {REASONS.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setReason(r.key)}
                    style={{
                      padding: '14px 16px',
                      border: reason === r.key ? '2px solid var(--primary)' : '1px solid var(--border, #e5e5e5)',
                      background: reason === r.key ? 'var(--primary-light, #fff9e6)' : 'white',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: reason === r.key ? 700 : 500,
                      color: reason === r.key ? 'var(--primary)' : 'var(--text, #111)',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>상세 내용 (선택)</div>
              <textarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="환불이 필요한 이유를 상세히 적어주세요"
                rows={4}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid var(--border, #e5e5e5)',
                  borderRadius: 10,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={closeModal}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  padding: 14,
                  background: 'var(--bg, #f5f5f5)',
                  color: 'var(--text, #111)',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={submit}
                disabled={isSubmitting}
                style={{
                  flex: 2,
                  padding: 14,
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                {isSubmitting ? '요청 중...' : '환불 요청'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
